(() => {
  'use strict';

  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const rootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const route = path => `${rootPath()}${String(path).replace(/^\//, '')}`;
  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));
  const searchHistoryKey = 'wokora-search-history-v1';

  const readHistory = () => {
    try {
      const value = JSON.parse(localStorage.getItem(searchHistoryKey) || '[]');
      return Array.isArray(value) ? value.filter(Boolean).slice(0, 5) : [];
    } catch { return []; }
  };
  const saveHistory = term => {
    const clean = String(term || '').trim();
    if (clean.length < 2) return;
    const next = [clean, ...readHistory().filter(item => item.toLowerCase() !== clean.toLowerCase())].slice(0, 5);
    try { localStorage.setItem(searchHistoryKey, JSON.stringify(next)); } catch {}
  };
  const clearHistory = () => {
    try { localStorage.removeItem(searchHistoryKey); } catch {}
  };

  const formatMoney = cents => {
    const numeric = Number(cents || 0);
    const value = numeric / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-BE', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(value);
    } catch {
      return `€${value.toFixed(2).replace('.', ',')}`;
    }
  };

  const imageUrl = product => {
    if (!product) return '';
    if (typeof product.image === 'string') return product.image;
    return product.image?.url || product.featured_image?.url || product.featured_image?.src || '';
  };

  const renderRecent = root => {
    const target = root.querySelector('[data-enterprise-search-recent]');
    if (!target) return;
    const history = readHistory();
    if (!history.length) {
      target.innerHTML = '';
      target.classList.remove('enterprise-search__recent');
      return;
    }
    target.classList.add('enterprise-search__recent');
    target.innerHTML = `
      <div class="enterprise-search__recent-head">
        <span class="enterprise-search__label" style="margin:0">Recent gezocht</span>
        <button class="enterprise-search__recent-clear" type="button" data-search-history-clear>Wissen</button>
      </div>
      <div class="enterprise-search__chips">
        ${history.map(term => `<a href="${route(`search?q=${encodeURIComponent(term)}&type=product`)}">${escapeHtml(term)}</a>`).join('')}
      </div>`;
  };

  const initSearch = root => {
    if (root.dataset.enterpriseSearchReady === 'true') return;
    root.dataset.enterpriseSearchReady = 'true';

    const input = root.querySelector('[data-enterprise-search-input]');
    const form = root.querySelector('[data-enterprise-search-form]');
    const panel = root.querySelector('[data-enterprise-search-panel]');
    const defaultView = root.querySelector('[data-enterprise-search-default]');
    const results = root.querySelector('[data-enterprise-search-results]');
    const persistent = root.dataset.enterpriseSearchPersistent === 'true';
    if (!input || !form || !panel || !defaultView || !results) return;

    let timer = 0;
    let controller = null;
    let activeTerm = '';

    const setBusy = busy => panel.setAttribute('aria-busy', busy ? 'true' : 'false');
    setBusy(false);

    const setOpen = open => {
      const shouldOpen = persistent || open;
      panel.hidden = !shouldOpen;
      input.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    };

    const showDefault = () => {
      controller?.abort();
      setBusy(false);
      results.innerHTML = '';
      defaultView.hidden = false;
      renderRecent(root);
      setOpen(true);
    };

    const showLoading = () => {
      setBusy(true);
      defaultView.hidden = true;
      results.innerHTML = '<div class="enterprise-search__loading">Zoeken in het assortiment…</div>';
      setOpen(true);
    };

    const renderResults = (term, payload) => {
      setBusy(false);
      const products = payload?.resources?.results?.products || [];
      const collections = payload?.resources?.results?.collections || [];
      const brands = [...new Set(products.map(product => String(product.vendor || '').trim()).filter(Boolean))].slice(0, 6);
      defaultView.hidden = true;

      if (!products.length && !collections.length) {
        results.innerHTML = `<div class="enterprise-search__empty"><strong>Geen resultaten voor “${escapeHtml(term)}”</strong><span>Probeer een productnaam, merk, SKU of categorie.</span></div>`;
        setOpen(true);
        return;
      }

      const productMarkup = products.slice(0, 6).map(product => {
        const image = imageUrl(product);
        const vendor = product.vendor ? `<span class="enterprise-search__product-vendor">${escapeHtml(product.vendor)}</span>` : '';
        return `<a class="enterprise-search__product" href="${escapeHtml(product.url || '#')}">
          <span class="enterprise-search__product-media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="enterprise-search__product-placeholder"></span>'}</span>
          <span class="enterprise-search__product-copy">${vendor}<span class="enterprise-search__product-title">${escapeHtml(product.title)}</span></span>
          <span class="enterprise-search__product-price">${formatMoney(product.price)}</span>
        </a>`;
      }).join('');

      const collectionMarkup = collections.slice(0, 6).map(collection =>
        `<a href="${escapeHtml(collection.url || '#')}"><span>${escapeHtml(collection.title)}</span><span aria-hidden="true">→</span></a>`
      ).join('');

      const brandMarkup = brands.map(brand =>
        `<a href="${route(`search?q=${encodeURIComponent(brand)}&type=product`)}">${escapeHtml(brand)}</a>`
      ).join('');

      const sideMarkup = (collections.length || brands.length) ? `<div class="enterprise-search__side">
        ${collections.length ? `<div class="enterprise-search__side-card"><span class="enterprise-search__label">Categorieën</span><div class="enterprise-search__collection-list">${collectionMarkup}</div></div>` : ''}
        ${brands.length ? `<div class="enterprise-search__side-card"><span class="enterprise-search__label">Merken in resultaten</span><div class="enterprise-search__brand-list">${brandMarkup}</div></div>` : ''}
      </div>` : '';

      const totalShown = products.length + collections.length;
      results.innerHTML = `<div class="enterprise-search__results-head"><strong>Zoekresultaten</strong><span>${totalShown} suggesties</span></div>
        <div class="enterprise-search__results-grid">
          <div class="enterprise-search__product-list">${productMarkup || '<div class="enterprise-search__empty"><span>Geen producten gevonden.</span></div>'}</div>
          ${sideMarkup}
        </div>
        <a class="enterprise-search__view-all" data-enterprise-search-view-all href="${route(`search?q=${encodeURIComponent(term)}&type=product`)}"><span>Bekijk alle resultaten voor “${escapeHtml(term)}”</span><span aria-hidden="true">→</span></a>`;
      setOpen(true);
    };

    const search = term => {
      controller?.abort();
      controller = new AbortController();
      activeTerm = term;
      showLoading();

      const params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product,collection');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'last');

      fetch(`${route('search/suggest.json')}?${params.toString()}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal
      })
        .then(response => {
          if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
          return response.json();
        })
        .then(payload => {
          if (input.value.trim() !== activeTerm) return;
          renderResults(term, payload);
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          setBusy(false);
          results.innerHTML = '';
          defaultView.hidden = false;
          setOpen(true);
        });
    };

    renderRecent(root);
    if (persistent) setOpen(true);

    input.addEventListener('focus', () => {
      const term = input.value.trim();
      if (term.length >= 2 && results.innerHTML.trim()) setOpen(true);
      else showDefault();
    });

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) {
        activeTerm = '';
        showDefault();
        return;
      }
      timer = window.setTimeout(() => search(term), 180);
    });

    input.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !persistent) {
        event.preventDefault();
        setOpen(false);
        input.blur();
      }
    });

    form.addEventListener('submit', () => saveHistory(input.value));

    root.addEventListener('click', event => {
      if (event.target.closest('[data-search-history-clear]')) {
        event.preventDefault();
        clearHistory();
        renderRecent(root);
        qsa('[data-enterprise-search]').forEach(renderRecent);
      }
      const viewAll = event.target.closest('[data-enterprise-search-view-all]');
      if (viewAll) saveHistory(input.value);
      const product = event.target.closest('.enterprise-search__product');
      if (product) saveHistory(input.value);
    });

    document.addEventListener('pointerdown', event => {
      if (persistent || root.contains(event.target)) return;
      setOpen(false);
    });
  };

  const initAll = context => qsa('[data-enterprise-search]', context).forEach(initSearch);
  initAll(document);

  document.addEventListener('shopify:section:load', event => initAll(event.target));

  document.addEventListener('click', event => {
    if (!event.target.closest('[data-search-history-clear]')) return;
    qsa('[data-enterprise-search]').forEach(renderRecent);
  });
})();
